<script lang="ts">
const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_formatted_value.name',
	description: '$t:blocks.display_formatted_value.description',
	icon: 'lucide:a-large-small',
	category: 'display',
	hidden: true,
	options: [
		{ name: 'fontFamily', type: 'enum', values: ['sans', 'serif', 'mono', 'display'] },
		{ name: 'weight', type: 'enum', values: ['normal', 'medium', 'semibold', 'bold'] },
	],

	fieldBinding: {
		supportedTypes: ['identifier', 'text', 'number'],
		defaultProps: ({ fieldTypeId }) => {
			if (fieldTypeId === 'identifier') {
				return {
					fontFamily: 'mono',
					weight: 'medium',
				};
			}
		},
	},
}
</script>
<script setup lang="ts">
import type { DisplayBlockProps } from '#layers/blocks/types/display-block';
import { computed } from 'vue';
import { isNil, useUserLocale } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { useFormattedDisplayValue } from '#layers/blocks/composables/use-formatted-display-value';
import { useTranslationUtils } from '#layers/i18n/public';
import { useDisplayTemplateNode } from './use-display-template-node';

type AcceptableValue = string | number;
type DisplayFormattedValueFontFamily = 'sans' | 'serif' | 'mono' | 'display';
type DisplayFormattedValueWeight = 'thin' | 'extralight' | 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black';

const props = withDefaults(defineProps<DisplayFormattedValueBlockProps>(), {
	fontFamily: 'sans',
	weight: 'normal',
});

interface DisplayFormattedValueBlockProps extends DisplayBlockProps<AcceptableValue | AcceptableValue[]> {
	fontFamily?: DisplayFormattedValueFontFamily;
	weight?: DisplayFormattedValueWeight;

	icon?: string;
	iconColor?: string;
	/**
	 * Format the value to title case.
	 */
	convertToTitleCase?: string;
}

const fontFamilyClasses = {
	sans: 'font-sans',
	serif: 'font-serif',
	mono: 'font-mono',
	display: 'font-display',
} satisfies Record<DisplayFormattedValueFontFamily, string>;

const fontWeightClasses = {
	thin: 'font-thin',
	extralight: 'font-extralight',
	light: 'font-light',
	normal: 'font-normal',
	medium: 'font-medium',
	semibold: 'font-semibold',
	bold: 'font-bold',
	extrabold: 'font-extrabold',
	black: 'font-black',
} satisfies Record<DisplayFormattedValueWeight, string>;

;

const { translateString } = useTranslationUtils();
const { language } = useUserLocale();
const content = useFormattedDisplayValue({
	value: () => props.value,
	convertToTitleCase: () => props.convertToTitleCase,
	language,
	translateString,
});
const { isNestedDisplayTemplateNode, displayTemplateNodeElement } = useDisplayTemplateNode(() => props.root);
const rootClasses = computed(() => {
	if (isNestedDisplayTemplateNode.value) {
		// The row centers this node for us, so we only lay out and truncate our content.
		return 'flex min-w-0 items-center gap-2';
	}

	return 'flex items-center gap-2 overflow-hidden';
});
const fontClasses = computed(() => [
	props.weight ? fontWeightClasses[props.weight] : undefined,
	props.fontFamily ? fontFamilyClasses[props.fontFamily] : undefined,
]);
</script>

<template>
	<DDisplayValueEmpty v-if="!$slots.default && isNil(value)" />

	<component :is="displayTemplateNodeElement" v-else :class="rootClasses">
		<DIcon
			v-if="icon"
			class="size-4 shrink-0"
			:name="icon"
			:style="{ color: iconColor }"
		/>
		<span
			:class="[
				fontClasses,
				{
					'truncate': !isNestedDisplayTemplateNode,
					'min-w-0 overflow-hidden text-ellipsis whitespace-pre': isNestedDisplayTemplateNode,
				},
			]"
		>
			<slot>
				{{ content }}
			</slot>
		</span>
	</component>
</template>
